import * as React from "react";
import {useForm, Controller} from "react-hook-form";
import {useState} from "react";
import {CustomerFactory} from "@connox/typescript-library";
import {getShopId} from "../lib/getShopId";
import Checkbox from "../lib/form/Checkbox/Checkbox";
import {Trans, useTranslation} from "react-i18next";
import styles from "./NewsletterRegistrationPage.module.scss";
import {emailRegExp} from "../lib/ModalWindow/ForgotPasswordModal";
import NewsletterBenefits from "../customer/account/newsletter/NewsletterBenefits";
import Spinner from "../utils/loader/Spinner";

const NewsletterRegistrationPage: React.FC = () => {
  const {t} = useTranslation("customerAccount");
  const {
    register,
    watch,
    handleSubmit,
    formState: {errors},
    control,
  } = useForm<{email: string; consent: boolean}>({mode: "onSubmit", defaultValues: {email: "", consent: false}});

  const [customerEmail, setCustomerEmail] = useState("");
  const [successState, setSuccessState] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const resendEmail = () => setSuccessState(false);

  return (
    <div>
      <div className={styles.banner}>
        <div className={styles.bannerContent}>
          <div className={styles.bannerContentBox}>
            <h2 className={styles.bannerContentTitle}>Newsletter</h2>
            <p className={styles.desktop}>
              <Trans i18nKey={"TwelvePercent"} ns="customerAccount">
                Jetzt zum Connox Newsletter anmelden und <b>12 %- Willkommensrabatt</b> sichern<sup>1</sup>
              </Trans>
            </p>
          </div>
          <img
            width="100%"
            height="180"
            alt={t("Im Newsletter über Neugkeiten, Trends und Angebote erfahren")}
            onLoad={(event: React.ChangeEvent<HTMLImageElement>) => event.target.removeAttribute("height")}
            src={"/media/Newsletter/Newsletteranmeldeseite-Header-1100x258.jpg"}
          />
        </div>
        <div className={styles.mobile}>
          <Trans i18nKey={"TwelvePercent"} ns="customerAccount">
            Jetzt zum Connox Newsletter anmelden und <b>12 %- Willkommensrabatt</b> sichern<sup>1</sup>
          </Trans>
        </div>
      </div>
      {successState ? (
        <div className={styles.form}>
          <h2>{t("Fast geschafft!")}</h2>
          <p>
            <Trans i18nKey={"EmailBestätigen"} values={{customerEmail}} ns="customerAccount">
              Bitte bestätigen Sie Ihre Anmeldung über den Link in der E-Mail, die wir an <b>{{customerEmail}}</b>{" "}
              gesendet haben
            </Trans>
          </p>
          <p>
            <Trans i18nKey="ResendMail">
              Wenn Sie keine E-Mail von uns erhalten haben, lassen Sie sich{" "}
              <span className={styles.resendLink} onClick={resendEmail}>
                eine neue E-Mail zuschicken
              </span>
              .
            </Trans>
          </p>
        </div>
      ) : (
        <form
          className={styles.form}
          onSubmit={handleSubmit((data) => {
            setIsLoading(true);
            void CustomerFactory.createOrUpdateCustomer(getShopId(), {email: data.email, newsletterFlag: true})
              .then(() => {
                // TODO show frame saying 'please check your email inbox'
                setCustomerEmail(data.email);
                setSuccessState(true);
              })
              .catch(() => {
                setSuccessState(false);
              })
              .finally(() => {
                setIsLoading(false);
              });
          })}
        >
          <p className={styles.emailLabel}>{t("Ihre E-Mail Adresse")}</p>
          <span className={styles.invalidInputMessage}>{errors.email && errors.email.message}</span>
          <input
            type="email"
            className={styles.input}
            {...register("email", {
              required: {
                value: true,
                message: t("Bitte ausfüllen"),
              },
              pattern: {
                value: emailRegExp,
                message: t("Invalid email address"),
              },
            })}
          />
          <div>
            <Controller
              control={control}
              render={({field}) => (
                <Checkbox
                  className={errors.consent ? styles.checkboxError : styles.checkbox}
                  color={"#75B537"}
                  checked={watch().consent}
                  onChange={(checked) => {
                    field.onChange(checked);
                  }}
                >
                  <span>
                    <Trans i18nKey="BinDamitEinverstanden" ns="customerAccount">
                      Ich bin damit einverstanden, dass Connox meine
                      <a href={t("privacy-link", {ns: "checkout"})} target="_blank" rel="noreferrer">
                        Daten
                      </a>
                      zu Marketingzwecken verarbeitet (Pflichtangabe)
                    </Trans>
                  </span>
                </Checkbox>
              )}
              rules={{required: true}}
              name={"consent"}
            />
            {errors.consent && (
              <span className={styles.mandatory}>
                {t("Bitte bestätigen Sie, dass Sie die Datenschutzbestimmungen gelesen haben")}
              </span>
            )}
          </div>
          <button formAction="submit" className={styles.submitBtn}>
            {isLoading ? <Spinner /> : <>{t("Jetzt kostenlos anmelden")}</>}
          </button>
        </form>
      )}

      <NewsletterBenefits />

      <div>
        <h3>{t("Sie haben unseren Newsletter bereits abonniert?")}</h3>
        <p>
          <Trans i18nKey="BereitsAbonniert" ns="customerAccount">
            <a href={`${t("/benutzerkonto.html")}?view=subscriptions`} target="_blank" rel="noreferrer">
              Hier
            </a>{" "}
            in Ihrem Kundenkonto können Sie Ihre Newsletter-Einstellungen bearbeiten, anpassen, zu welchen Themen Sie
            auf dem Laufenden gehalten werden möchten und sich wieder vom Newsletter abmelden.
          </Trans>
        </p>
      </div>
    </div>
  );
};

export default NewsletterRegistrationPage;
